import { useEffect, useState } from "react";
import { api, notifyError } from "../../lib/api";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "../../components/ui/dialog";
import { toast } from "sonner";
import { Save, Loader2, Mail, Send, Server, AlertTriangle } from "lucide-react";

const empty = {
  provider: "none",
  from_email: "",
  from_name: "Lastik Oteli",
  resend_api_key: "",
  sendgrid_api_key: "",
  smtp_host: "",
  smtp_port: 587,
  smtp_user: "",
  smtp_password: "",
  smtp_use_tls: true,
};

export default function MasterEmailSettings() {
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testOpen, setTestOpen] = useState(false);
  const [testEmail, setTestEmail] = useState("");
  const [testing, setTesting] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/master/email-settings");
      setForm({ ...empty, ...data });
    } catch (e) {
      notifyError(e);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put("/master/email-settings", { ...form, smtp_port: Number(form.smtp_port) || 587 });
      toast.success("E-posta ayarları kaydedildi");
    } catch (err) {
      notifyError(err);
    } finally {
      setSaving(false);
    }
  };

  const sendTest = async () => {
    setTesting(true);
    try {
      const { data } = await api.post("/master/email-settings/test", { to_email: testEmail });
      toast.success(
        data?.detail
          ? `Test gönderildi. ${data.detail}`
          : "Test e-postası gönderildi",
        { duration: 8000 }
      );
      setTestOpen(false);
    } catch (err) {
      notifyError(err);
    } finally {
      setTesting(false);
    }
  };

  if (loading) return <div className="p-8 text-center"><Loader2 className="h-6 w-6 animate-spin inline" /></div>;

  return (
    <div className="p-4 sm:p-8 max-w-3xl mx-auto space-y-6" data-testid="master-email-settings-page">
      <div>
        <div className="label-eyebrow text-amber-600">Bildirim</div>
        <h1 className="font-display font-black text-3xl sm:text-4xl tracking-tighter mt-2">E-posta Ayarları</h1>
        <p className="text-sm text-muted-foreground mt-2">
          Otomatik hatırlatma ve şifre sıfırlama e-postalarının hangi sağlayıcıdan gideceğini seçin.
        </p>
      </div>

      <form onSubmit={submit} className="space-y-6" data-testid="email-settings-form">
        <Card className="rounded-sm border-border p-6 space-y-4">
          <div>
            <Label className="label-eyebrow">Sağlayıcı</Label>
            <p className="text-xs text-muted-foreground mt-1">Resend ve SendGrid: API key. SMTP: kurumsal e-posta hesabınız.</p>
          </div>
          <Select value={form.provider} onValueChange={(v) => setForm({ ...form, provider: v })}>
            <SelectTrigger className="rounded-sm" data-testid="email-provider"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="none">Devre dışı (gönderim yok)</SelectItem>
              <SelectItem value="resend">Resend (API)</SelectItem>
              <SelectItem value="sendgrid">SendGrid (API)</SelectItem>
              <SelectItem value="smtp">Kurumsal SMTP (e.g. firma@firmadi.com)</SelectItem>
            </SelectContent>
          </Select>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-3 border-t border-border">
            <div className="space-y-1.5">
              <Label className="label-eyebrow">Gönderici Adı</Label>
              <Input value={form.from_name} onChange={(e) => setForm({ ...form, from_name: e.target.value })} className="rounded-sm" data-testid="email-from-name" />
            </div>
            <div className="space-y-1.5">
              <Label className="label-eyebrow">Gönderici E-posta</Label>
              <Input required={form.provider !== "none"} type="email" value={form.from_email} onChange={(e) => setForm({ ...form, from_email: e.target.value })} className="rounded-sm font-mono" placeholder="firma@firmadi.com" data-testid="email-from-address" />
            </div>
          </div>
        </Card>

        {form.provider === "resend" && (
          <Card className="rounded-sm border-border p-6 space-y-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-sm bg-black text-white flex items-center justify-center">
                <Send className="h-5 w-5" />
              </div>
              <div>
                <div className="font-display font-bold text-lg tracking-tight">Resend</div>
                <div className="text-xs text-muted-foreground">resend.com → API Keys</div>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="label-eyebrow">Resend API Key</Label>
              <Input type="password" value={form.resend_api_key} onChange={(e) => setForm({ ...form, resend_api_key: e.target.value })} className="rounded-sm font-mono" placeholder="re_..." data-testid="email-resend-key" />
            </div>
          </Card>
        )}

        {form.provider === "sendgrid" && (
          <Card className="rounded-sm border-border p-6 space-y-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-sm bg-[#1A82E2] text-white flex items-center justify-center">
                <Mail className="h-5 w-5" />
              </div>
              <div>
                <div className="font-display font-bold text-lg tracking-tight">SendGrid</div>
                <div className="text-xs text-muted-foreground">app.sendgrid.com → Settings → API Keys</div>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="label-eyebrow">SendGrid API Key</Label>
              <Input type="password" value={form.sendgrid_api_key} onChange={(e) => setForm({ ...form, sendgrid_api_key: e.target.value })} className="rounded-sm font-mono" placeholder="SG..." data-testid="email-sendgrid-key" />
            </div>
          </Card>
        )}

        {form.provider === "smtp" && (
          <Card className="rounded-sm border-border p-6 space-y-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-sm bg-foreground text-background flex items-center justify-center">
                <Server className="h-5 w-5" />
              </div>
              <div>
                <div className="font-display font-bold text-lg tracking-tight">Kurumsal SMTP</div>
                <div className="text-xs text-muted-foreground">Kendi sunucunuz veya barındırma sağlayıcınızın SMTP'si</div>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="sm:col-span-2 space-y-1.5">
                <Label className="label-eyebrow">SMTP Sunucu</Label>
                <Input
                  value={form.smtp_host}
                  onChange={(e) => setForm({ ...form, smtp_host: e.target.value })}
                  className="rounded-sm font-mono"
                  placeholder="ger-mellon.guzelhosting.com"
                  data-testid="smtp-host"
                />
                <p className="text-[10px] text-muted-foreground">
                  Paylaşımlı hosting (GüzelHosting, Natro vb.) için hosting panelinde <em>"Posta Sunucusu / Outgoing Server"</em> olarak verilen adı girin — genelde <code>mail.firmadi.com</code> CNAME'i değil, gerçek sunucu adı (örn. <code>ger-mellon.guzelhosting.com</code>) gerekir.
                </p>
              </div>
              <div className="space-y-1.5">
                <Label className="label-eyebrow">Port</Label>
                <Input type="number" value={form.smtp_port} onChange={(e) => setForm({ ...form, smtp_port: e.target.value })} className="rounded-sm font-mono" placeholder="465" data-testid="smtp-port" />
                <p className="text-[10px] text-muted-foreground">465 (SSL) önerilir, 587 (STARTTLS) alternatif</p>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="label-eyebrow">Kullanıcı Adı (E-posta)</Label>
                <Input value={form.smtp_user} onChange={(e) => setForm({ ...form, smtp_user: e.target.value })} className="rounded-sm font-mono" placeholder="firma@firmadi.com" data-testid="smtp-user" />
              </div>
              <div className="space-y-1.5">
                <Label className="label-eyebrow">Şifre</Label>
                <Input type="password" value={form.smtp_password} onChange={(e) => setForm({ ...form, smtp_password: e.target.value })} className="rounded-sm font-mono" data-testid="smtp-password" />
              </div>
            </div>
            <div className="flex items-center justify-between border-t border-border pt-3">
              <div>
                <Label className="label-eyebrow">STARTTLS Kullan</Label>
                <p className="text-xs text-muted-foreground mt-1">Port 587 için açık. Port 465 (SSL doğrudan) seçtiyseniz kapalı bırakın — kod yine SSL kullanır.</p>
              </div>
              <Switch checked={form.smtp_use_tls} onCheckedChange={(v) => setForm({ ...form, smtp_use_tls: v })} data-testid="smtp-use-tls" />
            </div>

            {form.from_email && form.smtp_user && form.from_email.toLowerCase() !== form.smtp_user.toLowerCase() && (
              <div className="border-l-2 border-amber-500 bg-amber-500/10 p-3 text-xs flex items-start gap-2">
                <AlertTriangle className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" />
                <span>
                  <strong>Dikkat:</strong> Gönderici E-posta (<code>{form.from_email}</code>) ve SMTP Kullanıcı Adı (<code>{form.smtp_user}</code>) farklı. Paylaşımlı hosting relay'lerinin büyük çoğunluğu yalnızca <strong>kimlik doğrulanan kullanıcının adresinden</strong> gönderime izin verir; aksi halde sessizce drop edebilir. İkisini de aynı yapın.
                </span>
              </div>
            )}
          </Card>
        )}

        {form.provider !== "none" && !form.from_email && (
          <div className="border-l-2 border-amber-500 bg-amber-500/10 p-3 text-xs flex items-start gap-2">
            <AlertTriangle className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" />
            <span>Sağlayıcı seçildi ama gönderici e-posta adresi boş. Lütfen yukarıdan girin.</span>
          </div>
        )}

        <div className="flex gap-3">
          <Button type="submit" disabled={saving} className="rounded-sm bg-foreground text-background hover:bg-foreground/90 h-11" data-testid="email-settings-save">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-4 w-4 mr-2" /> Kaydet</>}
          </Button>
          <Button type="button" variant="outline" onClick={() => setTestOpen(true)} className="rounded-sm h-11" disabled={form.provider === "none"} data-testid="email-test-btn">
            <Send className="h-4 w-4 mr-2" /> Test E-posta Gönder
          </Button>
        </div>
      </form>

      <Dialog open={testOpen} onOpenChange={setTestOpen}>
        <DialogContent className="rounded-sm">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight">Test E-postası Gönder</DialogTitle>
            <DialogDescription>Yapılandırmanın çalışıp çalışmadığını kontrol etmek için bir adrese test e-postası gönderin.</DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5">
            <Label className="label-eyebrow">Alıcı E-posta</Label>
            <Input type="email" value={testEmail} onChange={(e) => setTestEmail(e.target.value)} className="rounded-sm" data-testid="email-test-target" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTestOpen(false)} className="rounded-sm">İptal</Button>
            <Button onClick={sendTest} disabled={!testEmail || testing} className="rounded-sm bg-foreground text-background hover:bg-foreground/90" data-testid="email-test-send">
              {testing ? <Loader2 className="h-4 w-4 animate-spin" /> : "Gönder"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
